import React from 'react'

import { useUnit } from 'effector-react'

import { useSelector, useMediaQueries, useEffect, useRef, useCallback, useScrollPosition } from 'hooks'

import { $owner } from 'app/effector/contacts'

import { Card } from 'components/atoms/Card'
import { Gateway } from 'components/atoms/Gateway'
import { MobileLayout } from 'components/atoms/Layouts'
import { NavigationBar } from 'components/atoms/NavigationBar'
import { Paper } from 'components/atoms/Paper'
import { SemanticButton } from 'components/atoms/SemanticButton'

import { Modal } from 'components/molecules/Modal'
import { ProfileMenu } from 'components/molecules/ProfileMenu'
import { UserAvatar } from 'components/molecules/UserAvatar/UserAvatar'

import { useStickyTop } from 'app/containers/StickyTopProvider'

import './HeaderProfile.css'

const HeaderProfile = (): JSX.Element => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false)
  const owner = useUnit($owner)
  const client = useSelector((state) => state.client)
  const { isSticky } = useStickyTop()
  const scrollPosition = useScrollPosition(8, 0)

  const ref = useRef<HTMLDivElement | null>(null)
  const { desktop } = useMediaQueries()

  const toggleMenu = useCallback(() => {
    setIsProfileMenuOpen(!isProfileMenuOpen)
  }, [isProfileMenuOpen])

  const closeMenu = useCallback(() => {
    setIsProfileMenuOpen(false)
  }, [])

  useEffect(() => {
    if (!isSticky) {
      setIsProfileMenuOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition])

  useEffect(() => {
    const handleClickOutside = (event): void => {
      // Clicking outside the "Edit Avatar" modal shouldn't close the dropdown.
      const modalsContainer = document.querySelector('[data-gateway-destination="modals"]')

      if (modalsContainer && modalsContainer.contains(event.target)) {
        return
      }

      if (ref.current && !ref.current.contains(event.target)) {
        setIsProfileMenuOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  return (
    <div className="HeaderProfile-Wrapper" ref={ref}>
      <SemanticButton onClick={toggleMenu} aria-expanded={isProfileMenuOpen}>
        <UserAvatar
          size="40"
          firstName={owner?.first_name}
          lastName={owner?.last_name}
          image={client.avatar}
          dataTestId="headerAvatar"
        />
      </SemanticButton>
      {desktop ? (
        isProfileMenuOpen && (
          <Card className="HeaderProfile-Menu" color="background-default">
            <Paper top={24}>
              <ProfileMenu />
            </Paper>
          </Card>
        )
      ) : (
        <Gateway into="modals">
          <Modal open={isProfileMenuOpen} close={null}>
            <MobileLayout
              header={<NavigationBar rightPartText="Close" onRightPartClick={closeMenu} />}
              content={<ProfileMenu />}
            />
          </Modal>
        </Gateway>
      )}
    </div>
  )
}

export { HeaderProfile }
